import { useGetActiveOnlyShift } from '@api/pos/pos-work-period.api.rtk';

import {
  CalendarMonthTwoTone,
  // PeopleAltOutlined,
  CalendarTodayOutlined,
  ExpandMore,
  PersonOutlineOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  //   type MouseEvent,
} from 'react';

type InfoRowProps = {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
};

const InfoRow = ({ icon, label, value }: InfoRowProps) => (
  <Stack
    direction={label === 'Shift Operator' ? 'column' : 'row'}
    alignItems='left'
    flexWrap='wrap'
  >
    <Stack direction='row' gap={1}>
      <Box
        sx={{
          display: 'flex',
          color: 'text.secondary',
          '& svg': { fontSize: '12px', mt: '2px' },
        }}
      >
        {icon}
      </Box>

      <Typography
        sx={{
          fontSize: '12px',
          letterSpacing: '.4px',
          color: 'text.secondary',
          textWrap: 'nowrap',
        }}
      >
        {label}:
      </Typography>
    </Stack>

    <Typography
      sx={{
        fontSize: '12px',
        letterSpacing: '.4px',
        fontWeight: 600,
        color: value ? 'text.primary' : 'text.disabled',
        ml: label === 'Shift Operator' ? 2.5 : 1,
        textWrap: 'wrap',
        textOverflow: 'ellipsis',
      }}
    >
      {value ?? 'Not set'}
    </Typography>
  </Stack>
);

// ------------------------------------------------------
// Variant 1: Accordion
// ------------------------------------------------------
export const POSBOUserInfo = () => {
  const { data } = useGetActiveOnlyShift(
    { isActiveOnly: 1 },
    { refetchOnMountOrArgChange: true },
  );

  const userShiftData = data?.results[0];

  return (
    <Box sx={{ mt: 'auto', pb: '16px' }}>
      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        square
        sx={{
          backgroundColor: 'transparent',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ fontSize: '18px', rotate: '180deg' }} />
          }
          sx={{
            minHeight: 'unset',
            padding: '0 20.5px',
            '& .MuiAccordionSummary-content': { margin: '12px 0' },
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ color: 'primary.main' }}
          >
            <CalendarTodayOutlined
              sx={{ fontSize: '14px', color: 'primary.main' }}
            />
            <Typography
              sx={{ fontSize: '12px', letterSpacing: '.4px', fontWeight: 600 }}
            >
              Sales Date: {moment(userShiftData?.salesDate).format('LL') || '—'}
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: '0 20.5px 16px 20.5px' }}>
          <Stack spacing={1}>
            {/* <InfoRow
              icon={<WorkOutline />}
              label='Role'
              value={posSelectedRole}
            /> */}
            <InfoRow
              icon={<ScheduleOutlined />}
              label='Shift'
              value={userShiftData?.workPeriod}
            />
            <InfoRow
              icon={<PersonOutlineOutlined />}
              label='Shift Operator'
              value={userShiftData?.statedBy} // TODO: shiftOperator
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// ------------------------------------------------------
// Variant 2: Floating / Bubble
// ------------------------------------------------------
export const POSBOUserInfoFloating = () => {
  const { data } = useGetActiveOnlyShift(
    { isActiveOnly: 1 },
    { skip: true, refetchOnMountOrArgChange: true },
  );
  const userShiftData = data?.results[0];

  const BUBBLE_SIZE = 44;
  const EDGE_MARGIN = 0;

  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const dragStartY = useRef(0);
  const bubbleStartTop = useRef(0);

  const [side, setSide] = useState<'left' | 'right'>('right');
  const [top, setTop] = useState(120); // px from top
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // ---- Drag handlers (pointer events cover mouse + touch) ----
  const handlePointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    movedRef.current = false;
    dragStartY.current = e.clientY;
    bubbleStartTop.current = top;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!draggingRef.current) return;

    const deltaY = e.clientY - dragStartY.current;
    if (Math.abs(deltaY) > 3) movedRef.current = true;

    const maxTop = window.innerHeight - BUBBLE_SIZE - EDGE_MARGIN;
    const nextTop = Math.min(
      Math.max(bubbleStartTop.current + deltaY, EDGE_MARGIN),
      maxTop,
    );
    setTop(nextTop);

    // snap side based on cursor x position while dragging
    const midpoint = window.innerWidth / 2;
    setSide(e.clientX < midpoint ? 'left' : 'right');
  }, []);

  const handlePointerUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // ---- Click to open (ignored if it was actually a drag) ----
  const handleClick = () => {
    if (movedRef.current) return; // was a drag, not a click
    setAnchorEl((prev) => (prev ? null : bubbleRef.current));
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Box
        ref={bubbleRef}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        sx={{
          position: 'fixed',
          top,
          [side]: EDGE_MARGIN,
          zIndex: 1300,
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE ?? 'auto',
          borderRadius: side === 'right' ? '10px 0 0 10px' : '0 10px 10px 0',
          backgroundColor: open ? 'background.paper' : 'primary.main',
          color: open ? 'primary.main' : 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          cursor: 'grab',
          touchAction: 'none',
          userSelect: 'none',
          transition: 'background-color 0.15s ease',
          '&:active': { cursor: 'grabbing' },
          '&:hover': { backgroundColor: 'primary.dark' },
        }}
      >
        <CalendarMonthTwoTone sx={{ fontSize: '20px', cursor: 'pointer' }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: side === 'right' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: side === 'right' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              mx: 1,
              minWidth: 220,
            },
          },
        }}
      >
        <Box sx={{ padding: '16px' }}>
          <Stack spacing={1.25}>
            <InfoRow
              icon={<CalendarTodayOutlined />}
              label='Sales Date'
              value={userShiftData?.salesDate}
            />
            {/* <InfoRow
              icon={<PeopleAltOutlined />}
              label='Role'
              value={posSelectedRole}
            /> */}
            <InfoRow
              icon={<ScheduleOutlined />}
              label='Shift'
              value={userShiftData?.workPeriod}
            />
            <InfoRow
              icon={<PersonOutlineOutlined />}
              label='Shift Operator'
              value={userShiftData?.statedBy}
            />
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
